import { useApi } from '@src/app/context/Api';
import fields from '@src/entities/ActionPath/includes/fields.json';
import { fieldValidator } from '@src/shared/includes/validation';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useDebounce } from '@src/shared/ui/hooks';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isUndefined } from 'lodash';
import React, { useCallback, useEffect } from 'react';

const { name: nameField } = fields;

/**
 * Field: Name
 *
 * Provides the user with the ability to specify an action path name.
 *
 * @param actionPath
 * @param form
 * @returns {Element}
 * @constructor
 */
export function Name({
  actionPath = {
    name: '',
  },
  form = {
    clearErrors: () => {},
    getValues: () => {},
    register: () => {},
    setError: () => {},
    trigger: () => {},
    validate: () => {},
  },
}) {
  const name = form.getValues('name');

  const { useApiQuery } = useApi('ActionPath');

  const [GetValidActionPathName, { data, isFetching, isLoading }] = useApiQuery('GetValidActionPathName', {
    staleTime: 0,
  });

  /**
   * validateField
   */
  const validateField = () => {
    form.validate(
      'name',
      () => {},
      () => {}
    );
  };

  /**
   * sendRequest
   *
   * Makes a request to a remote API to validateCriteria whether the user provided action path name exists or not.
   *
   * @type {(function(): Promise<void>)|*}
   */
  const sendRequest = useCallback(async () => {
    if (form?.trigger('name') === true && name !== actionPath?.name) {
      await GetValidActionPathName({ name });
    }
  }, [name]);

  /**
   * When the data is received from the API, determine if the name is available or not.
   */
  useEffect(() => {
    if (!isUndefined(data)) {
      const isValid = data;

      if (isValid === true) {
        form?.clearErrors('name');
      }

      if (isValid === false) {
        form?.setError(
          'name',
          { message: 'Name already in use.', type: 'custom' },
          {
            shouldDirty: true,
            shouldFocus: true,
          }
        );
      }
    }
  }, [data, isLoading, isFetching]);

  /**
   * When the name or actionPath.name changes, validate the field.
   */
  useEffect(() => {
    validateField();
  }, [name, actionPath?.name]);

  const debouncedRequest = useDebounce(sendRequest, 250);

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/components/fields/Name/Name.jsx">
      <TextInput
        {...nameField}
        onChange={(event) => {
          if (event?.target?.value?.length >= 3) {
            if (form?.trigger('name') === true) {
              form?.setError(
                'name',
                { message: 'Verifying name is available...', type: 'custom' },
                { shouldFocus: true }
              );
            }
            debouncedRequest();
          }
        }}
        validators={{
          ...nameField.validators,
          ...{
            validate: {
              customValidator: (value) => {
                return fieldValidator(nameField, value);
              },
            },
          },
        }}
      />
    </ErrorHandler>
  );
}
